<template>
  <div v-if="isActive" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Overlay -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>
    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative w-full max-w-sm transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all">
        <!-- Header -->
        <button
          @click="closeModal"
          class="absolute top-3 right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
        >
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div class="flex flex-col items-center pt-6 pb-2 px-6">
          <div class="flex items-center justify-center mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="43" height="42" viewBox="0 0 43 42" fill="none">
                <path d="M21.5 3.5C18.0388 3.5 14.6554 4.52636 11.7775 6.44928C8.89967 8.37221 6.65665 11.1053 5.33212 14.303C4.00758 17.5007 3.66102 21.0194 4.33627 24.4141C5.01151 27.8087 6.67822 30.9269 9.12564 33.3744C11.5731 35.8218 14.6913 37.4885 18.0859 38.1637C21.4806 38.839 24.9993 38.4924 28.197 37.1679C31.3947 35.8433 34.1278 33.6003 36.0507 30.7225C37.9736 27.8446 39 24.4612 39 21C38.9949 16.3603 37.1495 11.912 33.8687 8.63126C30.588 5.35048 26.1397 3.5051 21.5 3.5ZM21.5 29.75C21.1539 29.75 20.8155 29.6474 20.5278 29.4551C20.24 29.2628 20.0157 28.9895 19.8832 28.6697C19.7508 28.3499 19.7161 27.9981 19.7836 27.6586C19.8512 27.3191 20.0178 27.0073 20.2626 26.7626C20.5073 26.5178 20.8191 26.3511 21.1586 26.2836C21.4981 26.2161 21.8499 26.2508 22.1697 26.3832C22.4895 26.5157 22.7628 26.74 22.9551 27.0277C23.1474 27.3155 23.25 27.6539 23.25 28C23.25 28.4641 23.0656 28.9092 22.7374 29.2374C22.4093 29.5656 21.9641 29.75 21.5 29.75ZM23.25 22.75C23.25 23.2141 23.0656 23.6592 22.7374 23.9874C22.4093 24.3156 21.9641 24.5 21.5 24.5C21.0359 24.5 20.5908 24.3156 20.2626 23.9874C19.9344 23.6592 19.75 23.2141 19.75 22.75V14C19.75 13.5359 19.9344 13.0907 20.2626 12.7626C20.5908 12.4344 21.0359 12.25 21.5 12.25C21.9641 12.25 22.4093 12.4344 22.7374 12.7626C23.0656 13.0907 23.25 13.5359 23.25 14V22.75Z" fill="#FF4265"/>
            </svg>
          </div>
          <h3 class="text-base font-semibold text-custom_gray text-center mb-1">Reportar propiedad</h3>
          <p class="text-base font-normal text-custom_gray text-center mb-2">Cuéntanos si tuviste algún problema con la propiedad para revisarla.</p>
        </div>
        <form @submit.prevent="submitReport" class="px-6 pb-4">
          <div class="flex flex-col gap-2 mb-3">
            <label v-for="motivo in reportReasons" :key="motivo.id" class="flex items-center gap-2 cursor-pointer text-sm text-gray-900">
              <input
                type="radio"
                v-model="reportData.reason"
                :value="motivo.id"
                class="w-4 h-4 text-tuin-100 bg-gray-100 border-gray-300 focus:ring-transparent focus:ring-0"
                required
              >
              {{ motivo.name }}
            </label>
          </div>
          <textarea
            v-model="reportData.comments"
            rows="3"
            placeholder="Compártenos más detalles."
            :class="[
              'w-full px-3 py-2 border rounded-lg text-sm focus:ring-1 mb-1 resize-none placeholder-gray-400',
              commentsError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-200 focus:border-tuin_base-0 focus:ring-tuin-100'
            ]"
          ></textarea>
          <span v-if="commentsError" class="text-red-500 text-xs">{{ commentsError }}</span>
          <button
            type="submit"
            :disabled="isSubmitDisabled"
            class="w-full py-2 rounded-lg text-white font-semibold text-base focus:border-tuin-100 bg-tuin-100 hover:bg-tuin_hover transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span v-if="submitting">Enviando...</span>
            <span v-else>Enviar reporte</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';

const props = defineProps({
  isActive: {
    type: Boolean,
    default: false
  },
  propertyId: {
    type: [String, Number],
    required: true
  },
  propertyName: {
    type: String,
    default: ''
  },
  reportReasons: {
    type: Array,
    default: () => []
  },
  agentId: {
    type: [String, Number],
    default: null
  }
});

const emit = defineEmits(['close', 'success']);

const store = useStore();
const session = computed(() => store.getters.getUserSession);

const submitting = ref(false);
const reportData = reactive({
  reason: '',
  comments: ''
});
const commentsError = ref('');

watch(() => reportData.comments, () => { commentsError.value = ''; });
watch(() => reportData.reason, () => { commentsError.value = ''; });

const isSubmitDisabled = computed(() => {
  if (!reportData.reason) return true;
  if (reportData.reason === 5 && !reportData.comments.trim()) return true;
  return submitting.value;
});

function closeModal() {
  emit('close');
  // Reset form
  reportData.reason = '';
  reportData.comments = '';
  commentsError.value = '';
}

async function submitReport() {
  if (!reportData.reason) {
    return;
  }
  if (reportData.reason === 5 && !reportData.comments.trim()) {
    commentsError.value = 'Campo obligatorio';
    return;
  } else {
    commentsError.value = '';
  }
  submitting.value = true;
  try {
    const payload = {
      reason_id: reportData.reason, // id del motivo seleccionado
      user_id: session.value.id,
      agent_id: props.agentId,
      property_id: props.propertyId,
      description: reportData.comments
    };
    const options = {
      headers: {
        Authorization: `Bearer ${session.value.token_api}`,
        "Content-Type": "application/json"
      },
    };
    const link = `${process.env.VUE_APP_API_BASE_URL}/reports`;
    const response = await axios.post(link, payload, options);
    if (response.status === 200 || response.status === 201) {
      emit('success', 'Reporte enviado correctamente');
      closeModal();
    } else {
      emit('danger', 'Error al enviar el reporte. Inténtalo de nuevo.');
    }
  } catch (error) {
    //console.error("Error al enviar reporte:", error);
    emit('danger', 'Error al enviar el reporte. Inténtalo de nuevo.');
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.fixed {
  position: fixed;
}
.inset-0 {
  top: 0; right: 0; bottom: 0; left: 0;
}
.z-50 { z-index: 50; }
.overflow-y-auto { overflow-y: auto; }
.bg-opacity-50 { background-color: rgba(0, 0, 0, 0.5); }
.transition-opacity { transition: opacity 0.15s cubic-bezier(0.4,0,0.2,1); }
</style> 